import {
  Grid,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import React, { useCallback, useContext, useEffect } from "react";
import { Header } from "./header";
import { RowComponent } from "./row";
import {
  INITIALIZE_TABLE,
  REMOVE,
  UPDATE_PAGE,
  UPDATE_ROWS_PER_PAGE,
  SELECT_ALL,
  UNSELECT_ALL,
} from "./state";
import { TableContext } from "./table";
import { Toolbar } from "./toolbar";
import { Row, RowWrapper, SearchFilterWrapper, TableProps } from "./types";
import { applyFilters, paginateFilter } from "./utils";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";

export type ActionColumn = "SELECT" | "EXPAND" | "EDIT" | "REMOVE";

const initRows = (rows: Row<any>[]): RowWrapper<any>[] => {
  return rows.map((row: Row<any>, index: number) => ({
    id: index,
    row,
  }));
};

//interface actionProps {
//    onClickAdd: () => void;
//    onShowAdd: () => void;
//}

export const TableWrapper: React.FC<TableProps> = ({
  data,
  headers,
  actionColumns = [],
  tableName,
  filterStatus,
  searchColumns: searchColumn = [],
  onRowClick = (row: any) => {},
  onRowSelected = (row: any) => {},
  onRowRemoved = (row: any) => {},
  onClickAdd = (row: any) => {},
  onShowAdd = (row: any) => {},
  onSearchDate = (name: string, value: MaterialUiPickersDate) => {},
  toolbar,
  filters,
  loading,
}) => {
  const { state, dispatch } = useContext(TableContext);

  const { rows, order, orderBy, page, rowsPerPage, filtersData, customFilter } =
    state;

  useEffect(() => {
    dispatch({ type: INITIALIZE_TABLE, rows: initRows(data) });
  }, [dispatch, data]);

  const actionProps = { onClickAdd, onShowAdd, onSearchDate, filterStatus };

  const handleChangePage = useCallback(
    (event: unknown, newPage: number) => {
      dispatch({ type: UPDATE_PAGE, page: newPage });
    },
    [dispatch]
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: UPDATE_ROWS_PER_PAGE,
        rowsPerPage: parseInt(event.target.value, 10),
      });
    },
    [dispatch]
  );

  //TODO: find a better way for this
  let tableContent = null;
  let tablePager = null;

  if (loading) {
    tablePager = (
      <TableRow>
        <TableCell
          variant="body"
          colSpan={headers.length + actionColumns?.length}
        >
          <CircularProgress size="1rem" />
        </TableCell>
      </TableRow>
    );
  } else {
    tablePager = null;
    tableContent = null;
  }

  //we can pass the decorated filter as a props
  let filteredRows: RowWrapper<any>[] = rows.slice();
  if (!!rows && rows.length > 0) {
    if (!!customFilter && !!filtersData) {
      filteredRows = customFilter(filteredRows, filtersData);
    }

    if (!!filters) {
      let searchFilterWrapper: SearchFilterWrapper = {
        headers: headers.map((header: any) => header.data),
        columnToSearch: searchColumn,
        criteriSearch: filtersData?.searchTerm,
      };

      filteredRows = applyFilters(
        {
          rows: filteredRows,
          order,
          orderBy,
          page,
          rowsPerPage,
          searchFilterWrapper,
        },
        filters
      );
    }
    // The condition below checks if the user is not in the first page of pagination
    // Orignally, there's a defect in searching when the user searches on the other pagination tab.
    // As a fix, we validate first if the user has a search item and if the result is not empty, if it has result,
    // then we bring the pagination to the default tab which is zero.
    const pageWithDataResult = (selectedPage: number) => {
      //state.page = selectedPage;

      tableContent = (
        <React.Fragment>
          {paginateFilter(
            filteredRows,
            selectedPage,
            rowsPerPage,
            order,
            orderBy
          ).map((wrapper: RowWrapper<any>, index: number) => {
            return (
              <RowComponent
                onClick={onRowClick}
                wrapper={wrapper}
                key={index}
                actions={actionColumns}
                onSelected={onRowSelected}
                onRemove={(wrapper: RowWrapper<any>) => {
                  onRowRemoved(wrapper.row);
                  dispatch({ type: REMOVE, row: wrapper });
                }}
              ></RowComponent>
            );
          })}
        </React.Fragment>
      );
    };

    if (page !== 0) {
      if (filtersData?.searchTerm) {
        if (filteredRows.length !== 0) {
          // 12142020 change
          //console.log("filteredRows:"+filteredRows.length);
          if (page > 1 && filteredRows.length < page * rowsPerPage) {
            pageWithDataResult(0);
          }
          // 12152020 change
          else {
            if (filteredRows.length < page * rowsPerPage) pageWithDataResult(0);
            else pageWithDataResult(page);
          }
        } else {
          // default pagination page
          pageWithDataResult(page);
        }
      } else {
        // default pagination page
        pageWithDataResult(page);
      }
    } else {
      // default pagination page
      pageWithDataResult(page);
    }
  }

  const handleSelectAll = (event: any) => {
    const selectAll = event.target.checked;
    selectAll
      ? dispatch({ type: SELECT_ALL, rows: filteredRows })
      : dispatch({ type: UNSELECT_ALL });
  };

  return (
    <div>
      <Grid container direction="row">
        <Grid container item xs={12}>
          {!!toolbar ? (
            <Toolbar
              tableName={tableName}
              type={toolbar}
              toolbarProps={actionProps}
            />
          ) : null}
        </Grid>
        <Grid container item xs={12}>
          <TableContainer>
            <Table>
              <Header
                actions={actionColumns}
                numSelected={2}
                order={order}
                orderBy={orderBy}
                headers={headers}
                onSelectAllClick={handleSelectAll}
              />
              <TableBody style={{ textAlign: "left" }}>
                {loading == true ? tablePager : tableContent}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            labelRowsPerPage="Records per page"
            rowsPerPageOptions={[10, 30, 50]}
            component="div"
            count={filteredRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Grid>
      </Grid>
    </div>
  );
};
